/** Optional customer text: empty / whitespace persists as null. */
export function optionalCustomerText(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

export function customerDisplayName(input: {
  first_name?: string | null;
  last_name?: string | null;
  phone_number: string;
}): string {
  return `${input.first_name || ''} ${input.last_name || ''}`.trim() || input.phone_number;
}

export type CustomerFormInput = {
  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  phone_number: string;
  landline_number?: string | null;
  email?: string | null;
  address: string;
  billing_address?: string | null;
  billing_email_address?: string | null;
  billing_contact_name?: string | null;
  payment_preference?: string | null;
  vat_number?: string | null;
  organization_id: number;
  organization_name: string;
};

export type CustomerInsertRow = {
  name: string;
  title: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string;
  landline_number: string | null;
  email: string | null;
  address: string;
  billing_address: string | null;
  billing_email_address: string | null;
  billing_contact_name: string | null;
  payment_preference: string | null;
  vat_number?: string | null;
  organization_id: number;
  organization_name: string;
};

const OPTIONAL_INSERT_KEYS = [
  'title',
  'first_name',
  'last_name',
  'landline_number',
  'email',
  'billing_address',
  'billing_email_address',
  'billing_contact_name',
  'payment_preference',
  'vat_number',
] as const;

/** Form / hook payload → customers insert row. Empty optional strings become null. */
export function mapCustomerFormToInsert(input: CustomerFormInput): CustomerInsertRow {
  return {
    name: optionalCustomerText(input.name) || customerDisplayName(input),
    title: optionalCustomerText(input.title),
    first_name: optionalCustomerText(input.first_name),
    last_name: optionalCustomerText(input.last_name),
    phone_number: input.phone_number,
    landline_number: optionalCustomerText(input.landline_number),
    email: optionalCustomerText(input.email),
    address: input.address,
    billing_address: optionalCustomerText(input.billing_address),
    billing_email_address: optionalCustomerText(input.billing_email_address),
    billing_contact_name: optionalCustomerText(input.billing_contact_name),
    payment_preference: optionalCustomerText(input.payment_preference),
    organization_id: input.organization_id,
    organization_name: input.organization_name,
    ...(optionalCustomerText(input.vat_number) != null
      ? { vat_number: optionalCustomerText(input.vat_number) }
      : {}),
  };
}

export type CustomerUpdateInput = Partial<CustomerFormInput>;

/** Edit payload → customers update row. Only provided keys are written; empty optional strings become null. */
export function mapCustomerFormToUpdate(input: CustomerUpdateInput): Partial<CustomerInsertRow> {
  const updates: Partial<CustomerInsertRow> = {};

  if ('name' in input) {
    const name = optionalCustomerText(input.name);
    if (name) updates.name = name;
  }
  if ('phone_number' in input && input.phone_number != null) {
    updates.phone_number = input.phone_number;
  }
  if ('address' in input && input.address != null) {
    updates.address = input.address;
  }
  if ('organization_id' in input && input.organization_id != null) {
    updates.organization_id = input.organization_id;
  }
  if ('organization_name' in input && input.organization_name != null) {
    updates.organization_name = input.organization_name;
  }

  for (const key of OPTIONAL_INSERT_KEYS) {
    if (!(key in input)) continue;
    const next = optionalCustomerText(input[key]);
    // vat_number is a new column — omit null so creates/edits still work
    // before the migration is applied. A provided value is always written.
    if (key === 'vat_number' && next == null) continue;
    updates[key] = next;
  }

  return updates;
}

export type CustomerRowLike = {
  id: string | number;
  name: string;
  title?: string | null;
  email?: string | null;
  phone_number?: string | null;
  landline_number?: string | null;
  address?: string | null;
  billing_address?: string | null;
  billing_email_address?: string | null;
  billing_contact_name?: string | null;
  payment_preference?: string | null;
  vat_number?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  created_at: string;
  updated_at: string;
};

export type UnifiedCustomerFields = {
  id: string;
  name: string;
  title?: string;
  email: string | null;
  phone: string;
  address: string;
  company?: string;
  reference?: string;
  notes?: string;
  billingAddress?: string | null;
  landline?: string | null;
  billingEmail?: string | null;
  billingContact?: string | null;
  paymentPreference?: string | null;
  vatNumber?: string | null;
  created_at: string;
  updated_at: string;
  source: 'api' | 'local';
};

/** DB / API row → unified Customer used by directory, workspace, and edit. */
export function mapCustomerRowToUnified(
  row: CustomerRowLike,
  source: 'api' | 'local' = 'local'
): UnifiedCustomerFields {
  return {
    id: String(row.id),
    name: row.name,
    title: row.title || undefined,
    email: row.email || null,
    phone: row.phone_number || '',
    address: row.address || '',
    company: row.organization_name || undefined,
    reference: row.organization_id != null ? String(row.organization_id) : undefined,
    notes: row.billing_address || undefined,
    billingAddress: row.billing_address ?? null,
    landline: row.landline_number ?? null,
    billingEmail: row.billing_email_address ?? null,
    billingContact: row.billing_contact_name ?? null,
    paymentPreference: row.payment_preference ?? null,
    vatNumber: row.vat_number ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    source,
  };
}

export function paymentPreferenceLabel(value?: string | null): string {
  if (!value) return '';
  const labels: Record<string, string> = {
    card: 'Card',
    cash: 'Cash',
    cheque: 'Cheque',
    EFT: 'EFT',
  };
  return labels[value] || value;
}

export type CustomerExtraDetail = {
  key: 'landline' | 'billingEmail' | 'billingContact' | 'paymentPreference' | 'vatNumber';
  label: string;
  value: string;
  href?: string;
};

export function isValidVatNumber(value: string): boolean {
  return /^[A-Za-z0-9]+$/.test(value.trim());
}

export function customerExtraDetails(customer: {
  landline?: string | null;
  billingEmail?: string | null;
  billingContact?: string | null;
  paymentPreference?: string | null;
  vatNumber?: string | null;
}): CustomerExtraDetail[] {
  const items: CustomerExtraDetail[] = [];
  if (customer.landline) {
    items.push({
      key: 'landline',
      label: 'Landline',
      value: customer.landline,
      href: `tel:${customer.landline}`,
    });
  }
  if (customer.billingEmail) {
    items.push({
      key: 'billingEmail',
      label: 'Billing email',
      value: customer.billingEmail,
      href: `mailto:${customer.billingEmail}`,
    });
  }
  if (customer.billingContact) {
    items.push({
      key: 'billingContact',
      label: 'Billing contact',
      value: customer.billingContact,
    });
  }
  if (customer.paymentPreference) {
    items.push({
      key: 'paymentPreference',
      label: 'Payment',
      value: paymentPreferenceLabel(customer.paymentPreference),
    });
  }
  if (customer.vatNumber) {
    items.push({
      key: 'vatNumber',
      label: 'VAT number',
      value: customer.vatNumber,
    });
  }
  return items;
}
